// eslint-disable-next-line no-unused-vars
import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { array, bool, func, string } from 'prop-types';
import { uniqueId } from 'lodash';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { SkeletonPlaceholder } from '@carbon/react';
import { ChevronDown, Tag } from 'lucide-react';

import { AnalyticsLink } from 'Components/shared';
import Img from 'Containers/Image/Image';

const StyledPromotionsComponent = styled.div`
  margin-top: 40px;

  h2 {
    font-size: 32px;
    line-height: 43px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  .manufacturers {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    max-width: calc(100vw - 30px);
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }

    .manufacturer {
      border: 1px solid #c6c6c6;
      border-radius: 35px;
      color: #c6c6c6;
      cursor: pointer;
      font-size: 14px;
      line-height: 19px;
      white-space: nowrap;
      padding: 7px 24px;
      background: transparent;

      &.active {
        border-color: #3a3a3a;
        color: #3a3a3a;
      }
    }
  }

  .promotions {
    display: flex;
    flex-wrap: wrap;
    gap: 40px;
    margin: 24px 0;

    .promotion {
      flex: 1 1 calc(50% - 40px);
      max-width: calc(50% - 20px);
      box-sizing: border-box;
      height: auto;
      background-color: #f3f2f2;
      border: 1px solid #e2e2e2;
      border-radius: 10px;

      &.hasBanner {
        .promotionInner {
          height: auto;

          .promotionHeader {
            margin-bottom: 0;
          }
        }

        img {
          max-width: 100%;
          height: auto;
        }
      }

      .promotionInner {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 20px;

        .promotionHeader {
          display: flex;
          margin-bottom: 20px;

          .promotionLabel {
            padding: 20px;
            background: #01A982;
            border-radius: 10px;

            svg {
              stroke: #fff;
            }
          }

          .promotionTitle {
            word-break: break-all;
            padding: 0 15px;
          }
        }

        .promotionFooter {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .availability {
            font-size: 14px;

            span {
              background-color: #c9e3c7;
              border-radius: 6px;
              color: #3a3a3a;
              padding: 0 18px;

              &.closeToEnd {
                background-color: #f5d9c0;
              }
            }
          }

          .actions {
            a {
              background-color: #fff;
              border: 1px solid #1f59a2;
              border-radius: 6px;
              display: inline-block;
              color: #1f59a2;
              padding: 10px 20px;
              text-align: center;

              svg {
                transform: rotate(270deg);
                display: none;
              }
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop } ) {
    h2 {
      font-size: 24px;
      line-height: 40px;
    }

    .promotions {
      gap: 20px;
      flex-direction: column;

      .promotion {
        flex: 1 1 100%;
        max-width: none;

        &.hasBanner {
          .promotionInner {
            .promotionHeader {
              margin-bottom: 20px;
            }
          }
        }

        .promotionInner {
          .promotionHeader {
            .promotionLabel {
              padding: 10px;
              height: fit-content;

              svg {
                width: 20px;
                height: 20px;
              }
            }
            .promotionTitle {
              font-size: 18px;
              line-height: 24px;
              font-weight: normal;
            }
          }

          .promotionFooter {
            .actions {
              a {
                span {
                  display: none;
                }

                svg {
                  display: inline-block;
                }
              }
            }
          }
        }
      }
    }
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 35px;
  width: 100%;
`;

const StyledPromotionSkeleton = styled(SkeletonPlaceholder)`
  height: 180px;
  width: 100%;
`;

const PromotionsComponent = ({
  promotions,
  filteredPromotions,
  manufacturers,
  handleActiveManufacturer,
  activeManufacturer,
  loading,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const manufacturersRef = useRef(null);

  useEffect(() => {
    const row = manufacturersRef.current;
    const handleWheel = event => {
      if (event.ctrlKey || event.deltaX || !event.deltaY) {
        return;
      }

      const previousScrollLeft = row.scrollLeft;
      row.scrollLeft += event.deltaY;
      if (row.scrollLeft !== previousScrollLeft) {
        event.preventDefault();
      }
    };

    row.addEventListener('wheel', handleWheel, { passive: false });
    return () => row.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <StyledPromotionsComponent theme={ theme }>
      <h2>{ t('navigation.weeklyPromotions') }</h2>
      <div className="manufacturers" ref={ manufacturersRef }>
        { loading ? <StyledSkeletonPlaceholder /> : (
          manufacturers.map(manufacturer => (
            <button
              key={ manufacturer.id }
              type="button"
              className={ `manufacturer ${ manufacturer.id === activeManufacturer ? 'active' : ''}` }
              onClick={ () => handleActiveManufacturer(manufacturer) }
            >
              { manufacturer.name }
            </button>
          ))
        ) }
      </div>
      { loading ? (
        <div className="promotions">
          { Array.from({ length: 4 }, () => (
            <div key={ uniqueId('loadingSkeleton_') } className="promotion">
              <StyledPromotionSkeleton />
            </div>
          )) }
        </div>
      ) : (
        <div className="promotions">
          { (filteredPromotions.length > 0 ? filteredPromotions : promotions).map(promotion => (
            <div className={ `promotion ${ (promotion.promotion_image_enabled && promotion.promotion_image) ? 'hasBanner' : '' }` } key={ promotion.erp_promotion_id }>
              <div className="promotionInner">
                <div className="promotionHeader">
                  <div className="promotionLabel">
                    <Tag size={ 55 } />
                  </div>
                  <div className="promotionTitle">
                    <h3>{ promotion.name }</h3>
                    <p>{ promotion.description }</p>
                  </div>
                </div>
                <div className="promotionFooter">
                  <div className="availability">
                    {`${ t('common.availableAnother') }: `}
                    <span className={ `${ promotion.close_to_promotion_end ? 'closeToEnd' : '' }` }>
                      { Math.min(promotion.days_to_promotion_end, 30) } { t('profile.days') }
                    </span>
                  </div>
                  <div className="actions">
                    <AnalyticsLink
                      to={ `/products?promotion=${ encodeURIComponent(promotion.erp_promotion_id) }` }
                      event={ { action: 'promotion_clicked' } }
                      isInternal
                    >
                      <span>{ t('common.viewPromotionalProducts') }</span>
                      <ChevronDown />
                    </AnalyticsLink>
                  </div>
                </div>
              </div>
              { (promotion.promotion_image_enabled && promotion.promotion_image) && (
                <Img
                  key={ uniqueId('promoBanner_') }
                  className="promotionBanner"
                  src={ promotion.promotion_image }
                  alt={ promotion.name }
                />
              )}
            </div>
          ))}
        </div>
      ) }
    </StyledPromotionsComponent>
  );
};

PromotionsComponent.propTypes = {
  promotions: array,
  filteredPromotions: array,
  manufacturers: array,
  handleActiveManufacturer: func,
  activeManufacturer: string,
  loading: bool,
};

PromotionsComponent.displayName = 'PromotionsComponent';

export default PromotionsComponent;
